import { Link, Text, View } from '@react-pdf/renderer'
import { RautReportIndication } from 'features/indicationTriage'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'features/reporting/pdfGeneration/components/shared/PageHeader'
import { PageSubheader } from 'features/reporting/pdfGeneration/components/shared/PageSubHeader'
import { ReportPage } from 'features/reporting/pdfGeneration/components/shared/ReportPage'
import { PDFStyles } from 'features/reporting/pdfGeneration/styles/PDFStyles'
import { SavedIndicationGraph } from 'features/reporting/types'
import {
  displayMin,
  displayWallLoss,
} from 'pages/reporting/reports/raut/sections/subSections/indicationDetailsComponents/indicationDisplay/utils/valueDisplayHelpers'
import React, { useCallback, useMemo } from 'react'
import {
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
} from 'types'

interface IndicationSummaryPageProps {
  indications: RautReportIndication[]
  savedIndicationGraphs: Record<string, SavedIndicationGraph>
  scanIndicationMode: boolean
  indicationToNominalMap: ReadonlyMap<
    string,
    GeckoMeasurement | GeckoMeasurementRange
  >
  measurementPrecision: number
  onRender: (info: { id: string; pageNumber: number }) => void
  jobNumber?: string
  revisionNumber?: number
  geometricUnit?: GeckoMeasurementUnit
  distanceUnit?: GeckoMeasurementUnit
  reviewerSignedOff?: boolean
  supervisorSignedOff?: boolean
  showComponentColumn?: boolean
  indicationToComponentMap?: ReadonlyMap<string, string>
}

export const IndicationSummaryPage: React.FC<IndicationSummaryPageProps> = ({
  indications,
  savedIndicationGraphs,
  scanIndicationMode,
  indicationToNominalMap,
  measurementPrecision,
  onRender,
  jobNumber,
  revisionNumber,
  geometricUnit = GeckoMeasurementUnit.Millimeters,
  distanceUnit = GeckoMeasurementUnit.Millimeters,
  reviewerSignedOff,
  supervisorSignedOff,
  showComponentColumn,
  indicationToComponentMap,
}) => {
  const dUnitStr = distanceUnit.id
  const gUnitStr = geometricUnit.id
  const decimals = distanceUnit === GeckoMeasurementUnit.Millimeters ? 0 : 3
  const firstPageRows = showComponentColumn ? 18 : 20
  const subsequentPageRows = 23
  const pageTitle = scanIndicationMode
    ? 'Scan Summary Table'
    : 'Indication Summary Table'
  const overviewText = scanIndicationMode
    ? 'This table summarizes all of the scans collected during the inspection. Click on a scan name to navigate to its detailed view.'
    : 'This table summarizes all indications found during the inspection. Click on an indication name to navigate to its detailed view in the Indication Details section.'

  const indicationTitleHandler = useCallback(
    (item: string) => (scanIndicationMode ? item.split('_')[0] : item),
    [scanIndicationMode]
  )

  const indicationChunks = useMemo(() => {
    const chunks: RautReportIndication[][] = []
    let currentIndex = 0

    if (indications.length > 0) {
      chunks.push(
        indications.slice(0, Math.min(firstPageRows, indications.length))
      )
      currentIndex = firstPageRows

      while (currentIndex < indications.length) {
        chunks.push(
          indications.slice(currentIndex, currentIndex + subsequentPageRows)
        )
        currentIndex += subsequentPageRows
      }
    }

    return chunks.length > 0 ? chunks : [[]]
  }, [firstPageRows, indications, subsequentPageRows])

  const getChunkStartIndex = useCallback(
    (chunkIndex: number) => {
      if (chunkIndex === 0) {
        return 0
      }

      return firstPageRows + (chunkIndex - 1) * subsequentPageRows
    },
    [firstPageRows, subsequentPageRows]
  )

  const renderTableChunk = useCallback(
    (chunk: RautReportIndication[], chunkIndex: number) => {
      const startIndex = getChunkStartIndex(chunkIndex)

      return (
        <View
          wrap={false}
          style={{
            width: '100%',
            marginTop: chunkIndex === 0 ? 16 : 0,
            fontSize: 8,
          }}>
          <View style={PDFStyles.tableHeader}>
            <View
              style={
                showComponentColumn
                  ? { ...PDFStyles.tableHeaderCell15, width: '10%' }
                  : PDFStyles.tableHeaderCell15
              }>
              <Text>{scanIndicationMode ? 'Scan' : 'Indication'}</Text>
            </View>
            {showComponentColumn && (
              <View
                style={{
                  width: '15%',
                  fontWeight: 'bold',
                  paddingRight: 8,
                  fontSize: 8,
                }}>
                <Text>Component</Text>
              </View>
            )}
            <View style={PDFStyles.tableHeaderCell20}>
              <Text>X Range ({dUnitStr})</Text>
            </View>
            <View style={PDFStyles.tableHeaderCell20}>
              <Text>Y Range ({dUnitStr})</Text>
            </View>
            <View style={PDFStyles.tableHeaderCell20}>
              <Text>Minimum ({gUnitStr})</Text>
            </View>
            <View style={PDFStyles.tableHeaderCell20}>
              <Text>% Wall Loss</Text>
            </View>
            <View style={PDFStyles.tableHeaderCell20}>
              <Text>DRA Rating</Text>
            </View>
          </View>

          {chunk.map((indication, index) => {
            const globalIndex = startIndex + index
            const nominalForThisIndication = indicationToNominalMap.get(
              indication.id
            )

            return (
              <View
                key={indication.id}
                style={
                  globalIndex % 2 === 0
                    ? PDFStyles.tableRowEven
                    : PDFStyles.tableRowOdd
                }>
                <View
                  style={
                    showComponentColumn
                      ? { ...PDFStyles.tableCell15, width: '10%' }
                      : PDFStyles.tableCell15
                  }>
                  <Link src={`#${indication.id}`} style={PDFStyles.link}>
                    <Text>{indicationTitleHandler(indication.title)}</Text>
                  </Link>
                </View>
                {showComponentColumn && (
                  <View style={{ width: '15%', paddingRight: 8, fontSize: 8 }}>
                    <Text>
                      {indicationToComponentMap?.get(indication.id) ?? ''}
                    </Text>
                  </View>
                )}
                <View style={PDFStyles.tableCell20}>
                  <Text>
                    {indication.selection
                      ? `${indication.selection.x.min.toNumber(distanceUnit).toFixed(decimals)} - ${indication.selection.x.max.toNumber(distanceUnit).toFixed(decimals)}`
                      : 'N/A'}
                  </Text>
                </View>
                <View style={PDFStyles.tableCell20}>
                  <Text>
                    {indication.selection
                      ? `${indication.selection.y.min.toNumber(distanceUnit).toFixed(decimals)} - ${indication.selection.y.max.toNumber(distanceUnit).toFixed(decimals)}`
                      : 'N/A'}
                  </Text>
                </View>
                <View style={PDFStyles.tableCell20}>
                  <Text>
                    {indication.customInfo.stats?.minimum &&
                      displayMin(
                        indication.customInfo.stats.minimum,
                        geometricUnit,
                        measurementPrecision,
                        false
                      )}
                  </Text>
                </View>
                <View style={PDFStyles.tableCell20}>
                  <Text>
                    {indication.customInfo.stats?.minimum &&
                      displayWallLoss(
                        indication.customInfo.stats.minimum,
                        nominalForThisIndication ?? GeckoMeasurement.Inf(),
                        geometricUnit,
                        measurementPrecision,
                        false
                      )}{' '}
                  </Text>
                </View>
                <View style={PDFStyles.tableCell20}>
                  <Text>
                    {`${savedIndicationGraphs[indication.id]?.dra_rating ?? indication.customInfo.dra_rating} - ${savedIndicationGraphs[indication.id]?.sub_rating ?? indication.customInfo.sub_rating}`}
                  </Text>
                </View>
              </View>
            )
          })}
        </View>
      )
    },
    [
      dUnitStr,
      decimals,
      distanceUnit,
      getChunkStartIndex,
      geometricUnit,
      gUnitStr,
      indicationTitleHandler,
      indicationToComponentMap,
      indicationToNominalMap,
      measurementPrecision,
      savedIndicationGraphs,
      scanIndicationMode,
      showComponentColumn,
    ]
  )

  return (
    <>
      {indicationChunks.map((chunk, chunkIndex) => {
        const isFirstPage = chunkIndex === 0
        const isLastPage = chunkIndex === indicationChunks.length - 1

        return (
          <ReportPage
            key={`indications-${chunkIndex}`}
            id={isFirstPage ? 'indications' : undefined}
            size='A4'
            tracker={
              isFirstPage ? { sectionId: 'indications', onRender } : undefined
            }
            header={<PageHeader title={pageTitle} />}
            contentStyle={{ padding: 10 }}
            footer={
              <PageFooter
                jobNumber={jobNumber}
                revisionNumber={revisionNumber}
                prelim={!(reviewerSignedOff && supervisorSignedOff)}
              />
            }>
            {isFirstPage && (
              <>
                <PageSubheader title='Overview' align='left' color='black' />
                <Text style={PDFStyles.text}>{overviewText}</Text>
              </>
            )}

            {renderTableChunk(chunk, chunkIndex)}

            {isLastPage && (
              <View style={PDFStyles.summaryContainer}>
                <Text style={PDFStyles.text}>
                  Total {scanIndicationMode ? 'Scans' : 'Indications'}{' '}
                  {indications.length}
                </Text>
              </View>
            )}
          </ReportPage>
        )
      })}
    </>
  )
}
